import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { EllipsisVerticalIcon, ShieldCheckIcon } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { updateUserMutation, getUserQuery, getUserGroupsQuery, makeAdminMutation } from '@/queries/userMutation';
import type { UserData } from '@/queries/userMutation';

export const Route = createFileRoute('/_app/settings/users/$id/')({
  component: RouteComponent,
})


function RouteComponent() {
  const { id } = Route.useParams();
  const { data: userData, isLoading } = useQuery(getUserQuery(id));
  const { data: userGroups = [] } = useQuery(getUserGroupsQuery());
  const updateUser = updateUserMutation();
  const makeAdmin = makeAdminMutation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<UserData>({
    id: '',
    emailAddress: '',
    firstName: '',
    lastName: '',
    userName: '',
    userGroupId: '',
    textNumber: '',
    active: true,
    enableMFA: false,
    isAdmin: false
  });

  useEffect(() => {
    if (userData) {
      setFormData(userData);
    }
  }, [userData]);

  const handleInputChange = (field: keyof UserData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      await updateUser.mutateAsync({ id, data: formData });
      // Show success message or handle success
      navigate({ to: '/settings/users' });
    } catch (error) {
      // Handle error
      console.error('Failed to update user:', error);
    }
  };

  const handleMakeAdmin = async () => {
    try {
      await makeAdmin.mutateAsync({ id, isAdmin: !formData.isAdmin });
      setFormData(prev => ({ ...prev, isAdmin: !prev.isAdmin }));
    } catch (error) {
      console.error('Failed to update admin status:', error);
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <header
        className={`sticky top-0 flex shrink-0 items-center gap-2 p-2 bg-white border-b-4 border-l-gray-200 border-r-gray-200 border-t-gray-200 border-t-2`}
      >
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mr-2 h-4" />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem className="hidden md:block">
              <BreadcrumbLink asChild>
                <Link to='/settings/users'>
                  Users
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>

            <BreadcrumbSeparator className="hidden md:block" />
            <BreadcrumbItem>
              <BreadcrumbPage>
                Edit User
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="flex items-center gap-2 ml-auto mr-5">
          <Button
            onClick={handleSave}
            disabled={updateUser.isPending}
            className="bg-green-500 hover:bg-green-600 text-white"
          >
            {updateUser.isPending ? 'Saving...' : 'Save'}
          </Button>

          {!formData.isAdmin ? (
            <Button
              onClick={handleMakeAdmin}
              disabled={makeAdmin.isPending}
              className="bg-yellow-500 hover:bg-yellow-600 text-white"
            >
              {makeAdmin.isPending ? 'Processing...' : 'Make Admin'}
            </Button>
          ) : (
            <Button
              onClick={handleMakeAdmin}
              disabled={makeAdmin.isPending}
              variant="outline"
              className="border-red-500 text-red-500 hover:bg-red-50"
            >
              {makeAdmin.isPending ? 'Processing...' : 'Remove Admin'}
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                Actions <EllipsisVerticalIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>Quick Actions</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-red-500">Delete User</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="h-full w-full p-6 bg-gray-100">
        <Card className="max-w-4xl">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-medium">Edit User</CardTitle>
              <div className="flex items-center gap-2">
                {formData.isAdmin && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <ShieldCheckIcon className="h-3 w-3" />
                    Administrator
                  </Badge>
                )}
                {formData.active && (
                  <Badge variant="default" className="bg-green-100 text-green-800">
                    Active
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {formData.isAdmin && (
              <Alert className="border-blue-200 bg-blue-50">
                <ShieldCheckIcon className="h-4 w-4" />
                <AlertDescription>
                  This user is an Administrator. As an administrator this user has access to everything in CoolFocus.
                </AlertDescription>
              </Alert>
            )}

            {/* Active Checkbox */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="active"
                checked={formData.active}
                onCheckedChange={(checked) => handleInputChange('active', checked as boolean)}
              />
              <Label htmlFor="active" className="text-sm font-medium">
                Active
              </Label>
            </div>

            {/* User Name */}
            <div>
              <Label htmlFor="userName" className="text-sm font-medium">
                User Name
              </Label>
              <Input
                id="userName"
                value={formData.userName || ''}
                onChange={(e) => handleInputChange('userName', e.target.value)}
                className="mt-1"
              />
            </div>

            {/* First and Last Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName" className="text-sm font-medium">
                  First Name
                </Label>
                <Input
                  id="firstName"
                  value={formData.firstName || ''}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="lastName" className="text-sm font-medium">
                  Last Name
                </Label>
                <Input
                  id="lastName"
                  value={formData.lastName || ''}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Text Number and MFA */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="textNumber" className="text-sm font-medium">
                  Text Number
                </Label>
                <Input
                  id="textNumber"
                  value={formData.textNumber || ''}
                  onChange={(e) => handleInputChange('textNumber', e.target.value)}
                  className="mt-1"
                />
              </div>

              <div className="flex items-center space-x-2 mt-6">
                <Checkbox
                  id="enableMFA"
                  checked={formData.enableMFA}
                  onCheckedChange={(checked) => handleInputChange('enableMFA', checked as boolean)}
                />
                <Label htmlFor="enableMFA" className="text-sm font-medium">
                  Enable MFA
                </Label>
              </div>
            </div>

            {/* Email Address */}
            <div>
              <Label htmlFor="emailAddress" className="text-sm font-medium">
                Email Address
              </Label>
              <Input
                id="emailAddress"
                type="email"
                value={formData.emailAddress || ''}
                onChange={(e) => handleInputChange('emailAddress', e.target.value)}
                className="mt-1"
              />
            </div>

            {/* User Group */}
            <div>
              <Label htmlFor="userGroup" className="text-sm font-medium">
                User Group
              </Label>
              <Select
                value={formData.userGroupId || ''}
                onValueChange={(value) => handleInputChange('userGroupId', value)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select user group..." />
                </SelectTrigger>
                <SelectContent>
                  {userGroups.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
